import React, { useState, useEffect, useCallback } from 'react';
import AntTable from 'common_components/AntTable';
import EditButtonRenderer from 'common_components/EditButtonRenderer';
import { dateTimeTimestamptzFormatter } from 'common_components/AntTable/tableUtils';
import TableToolbar from 'common_components/AntTable/Toolbar';
import { push } from 'connected-react-router';
import permission from 'permissions';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'reducers/user';
import {
  useDeleteUserMutation,
  useDestroyUserMutation,
} from 'reducers/user/api';
import { useErrorHandler } from 'common_components/ErrorContext';
import { Sorter } from 'utils/sorter';
import styled from 'styled-components';
import { selectTableStateById } from 'reducers/table';
import { message } from 'antd';

const UserTableComponent = (props) => {
  const { rowData, isLoading } = props;
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const tableIdSelector = useSelector((state) =>
    selectTableStateById(state, 'user')
  );

  const [showDelete, setShowDelete] = useState(false);

  const dispatch = useDispatch();
  const { setErrorData } = useErrorHandler();
  const user = useSelector(getCurrentUser);
  const [
    deleteUser,
    {
      isSuccess: isSuccessDeleting,
      isUninitialized: isUninitDeleting,
      error: errorDeleting,
      isFetching: isDeleting,
    },
  ] = useDeleteUserMutation();
  const [
    destroyUser,
    {
      isSuccess: isSuccessDestroying,
      isUninitialized: isUninitDestroying,
      error: errorDestroying,
      isFetching: isDestroying,
    },
  ] = useDestroyUserMutation();

  useEffect(() => {
    if (errorDeleting) {
      setErrorData(errorDeleting);
    }
    if (errorDestroying) {
      setErrorData(errorDestroying);
    }
  }, [errorDeleting, errorDestroying, setErrorData]);

  useEffect(() => {
    if (!isUninitDeleting && isSuccessDeleting) {
      message.success('Data was successfully deleted');
      clearSelectedRows();
    }
  }, [isSuccessDeleting, isUninitDeleting]);

  useEffect(() => {
    if (!isUninitDestroying && isSuccessDestroying) {
      message.success('Data was successfully deleted');
    }
  }, [isSuccessDestroying, isUninitDestroying]);

  const onSearch = useCallback(() => {
    clearSelectedRows();
  }, []);

  const onCreate = () => {
    dispatch(push(`/app/delivery_mng/dntas/create`));
  };

  const handleEditClicked = (cell, readOnly) => {
    dispatch(push(`/app/delivery_mng/dntas/${cell.id}`));
  };

  const onDelete = (isPermanentDelete) => {
    // TODO: Bulk delete
    for (let row of selectedRows) {
      if (isPermanentDelete) {
        destroyUser({ id: row.id });
      } else {
        deleteUser({ id: row.id, is_active: false });
      }
    }

    clearSelectedRows();
  };

  const onReload = () => {
    props.refetchData();
    clearSelectedRows();
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setShowDelete(false);
  };

  const [columns, setColumns] = useState(() => {
    let cols = [
      {
        title: '',
        dataIndex: 'edit',
        align: 'center',
        render: (text, record) => {
          return (
            <EditButtonRenderer data={record} onClick={handleEditClicked} />
          );
        },
        width: 50,
      },
      {
        title: 'CALLING NO.',
        dataIndex: 'callNo',
        width: 100,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'DATE',
        dataIndex: 'date',
        width: 120,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'YARD',
        dataIndex: 'yard',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'REGION',
        dataIndex: 'region',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'CLUSTER',
        dataIndex: 'cluster',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'BZP',
        dataIndex: 'bzp',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'LOAD',
        dataIndex: 'load',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'SUB JOB',
        dataIndex: 'subjob',
        width: 80,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'SUBJOB & TIME',
        dataIndex: 'subjob_time',
        width: 180,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Updated time',
        dataIndex: 'updated_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
      {
        title: 'Created time',
        dataIndex: 'created_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
    ];

    // Add Show/Hide status from Redux store
    if (tableIdSelector) {
      cols = cols.map((c) => ({
        ...c,
        ...tableIdSelector.find((t) => t.dataIndex === c.dataIndex),
      }));
    }

    return cols;
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);

      if (permission.checkPermission(user, 'dnta', ['d'])) {
        setShowDelete(selectedRows.length > 0 ? true : false);
      }
    },
  };

  return (
    <Container>
      <TableToolbar
        onSearch={onSearch}
        onCreate={onCreate}
        onDelete={onDelete}
        onReload={onReload}
        showDelete={showDelete}
        showReload={true}
        isLoading={isLoading}
        isDeleting={isDeleting || isDestroying}
        showPermanentDelete={permission.isSuperAdmin(user)}
        showAdd={permission.checkPermission(user, 'dnta', ['c'])}
        columns={columns}
        setColumns={setColumns}
      />
      <AntTable
        tableId={'dnta'}
        rowSelection={rowSelection}
        bordered
        showSorterTooltip={false}
        rowKey='id'
        loading={isLoading}
        columns={columns}
        dataSource={rowData || []}
        scroll={{ x: 'max-content' }}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 0 10px;
`;

export default UserTableComponent;
